import {
	PRIMARY_PROGRESS_LIVE_SELECTOR,
	PRIMARY_PROGRESS_COUNT_SELECTOR,
	PRIMARY_PROGRESS_CURRENT_SELECTOR,
	PRIMARY_PROGRESS_NAME_SELECTOR,
	PRIMARY_PROGRESS_PERCENT_SELECTOR,
	PRIMARY_PROGRESS_TITLE_SELECTOR,
	PRIMARY_PROGRESS_WRAPPER_SELECTOR,
} from './sidebar-constants.js';
import {
	isTruthyAttributeValue,
	toPositiveInteger,
} from './sidebar-utils.js';

const PRIMARY_PROGRESS_CURRENT_CLASS = PRIMARY_PROGRESS_CURRENT_SELECTOR.replace(/^\./, '');
const PRIMARY_PROGRESS_COUNT_CLASS = PRIMARY_PROGRESS_COUNT_SELECTOR.replace(/^\./, '');
const PRIMARY_PROGRESS_NAME_CLASS = PRIMARY_PROGRESS_NAME_SELECTOR.replace(/^\./, '');

/**
* Responsibility:
* - Resolve the grouped progress-bar wrapper for one configurator form.
*
* Touches:
* - Queries the rendered GF progress wrapper by form id and custom grouped-progress class.
*
* Guarantees:
* - Returns the grouped progress wrapper or `null` when unavailable.
*/
function resolveProgressBarWrapper(formId) {
	const progressBarWrapper = document.querySelector(`#gf_progressbar_wrapper_${formId}${PRIMARY_PROGRESS_WRAPPER_SELECTOR}`);
	return progressBarWrapper instanceof HTMLElement ? progressBarWrapper : null;
}

/**
* Responsibility:
* - Resolve the configurator-owned live region inside the grouped progress bar.
*
* Touches:
* - Reads only the custom live region rendered by the plugin-owned grouped progress markup.
*
* Guarantees:
* - Returns the grouped progress live region or `null`.
*/
function resolveProgressLiveRegion(progressBarWrapper) {
	const liveRegion = progressBarWrapper?.querySelector(PRIMARY_PROGRESS_LIVE_SELECTOR);
	return liveRegion instanceof HTMLDivElement ? liveRegion : null;
}

/**
* Responsibility:
* - Resolve the translated grouped progress labels from the rendered wrapper metadata.
*
* Touches:
* - Reads plugin-rendered `data-progress-*` labels from the grouped progress wrapper.
*
* Guarantees:
* - Returns safe fallback labels when the wrapper metadata is missing.
*/
function resolveProgressLabels(progressBarWrapper) {
	return {
		stepLabel: String(progressBarWrapper?.dataset?.progressStepLabel || '').trim() || 'Step',
		ofLabel: String(progressBarWrapper?.dataset?.progressOfLabel || '').trim() || 'of',
		completedLabel: String(progressBarWrapper?.dataset?.progressCompletedLabel || '').trim() || 'Completed!',
	};
}

/**
* Responsibility:
* - Rebuild the grouped progress title markup from explicit state on every sync pass.
*
* Touches:
* - Replaces the plugin-owned title node contents with either normal step/count spans or the completed label.
*
* Guarantees:
* - Prevents stale "Step X of Y" markup from lingering once the final grouped step is reached.
*/
function renderProgressTitle(progressTitleElement, labels, {
	isCompletedState = false,
	currentStepIndex = 1,
	stepCount = 1,
	pageName = '',
} = {}) {
	if (!(progressTitleElement instanceof HTMLElement)) {
		return;
	}

	if (isCompletedState) {
		progressTitleElement.textContent = labels.completedLabel;
		return;
	}

	const currentPageElement = document.createElement('span');
	currentPageElement.className = PRIMARY_PROGRESS_CURRENT_CLASS;
	currentPageElement.textContent = String(currentStepIndex);

	const pageCountElement = document.createElement('span');
	pageCountElement.className = PRIMARY_PROGRESS_COUNT_CLASS;
	pageCountElement.textContent = String(stepCount);

	const pageNameElement = document.createElement('span');
	pageNameElement.className = PRIMARY_PROGRESS_NAME_CLASS;
	pageNameElement.textContent = pageName !== '' ? ` - ${pageName}` : '';

	progressTitleElement.replaceChildren(
		document.createTextNode(`${labels.stepLabel} `),
		currentPageElement,
		document.createTextNode(` ${labels.ofLabel} `),
		pageCountElement,
		pageNameElement
	);
}

/**
* Responsibility:
* - Build the live-region announcement for the grouped progress bar.
*
* Touches:
* - Used only by `syncPrimaryProgressBar()` when updating the plugin-owned progress live region.
*
* Guarantees:
* - Announces "Completed!" on the final grouped step and otherwise preserves the step/count/page label summary.
*/
function buildAccessibleProgressText(labels, {
	isCompletedState = false,
	currentStepIndex = 1,
	stepCount = 1,
	pageName = '',
} = {}) {
	if (isCompletedState) {
		return labels.completedLabel;
	}

	return pageName !== ''
		? `${labels.stepLabel} ${currentStepIndex} ${labels.ofLabel} ${stepCount}, ${pageName}`
		: `${labels.stepLabel} ${currentStepIndex} ${labels.ofLabel} ${stepCount}`;
}

/**
* Responsibility:
* - Synchronize the grouped custom progress bar that mirrors the native GF progress-bar styling.
*
* Touches:
* - Updates only the plugin-owned grouped progress title spans, live region, and fill node inside the rendered form.
*
* Guarantees:
* - Keeps the form-embedded progress bar aligned with grouped primary-step counts instead of raw GF page counts.
*/
export function syncPrimaryProgressBar(app, primaryStepStates) {
	const formId = toPositiveInteger(app?.config?.formId);
	if (formId <= 0) {
		return;
	}

	const progressBarWrapper = resolveProgressBarWrapper(formId);
	if (!(progressBarWrapper instanceof HTMLElement)) {
		return;
	}

	const safePrimaryStepStates = Array.isArray(primaryStepStates) ? primaryStepStates : [];
	if (safePrimaryStepStates.length <= 0) {
		return;
	}

	const activeStepIndex = safePrimaryStepStates.findIndex((stepState) => stepState.current === true);
	const currentStepIndex = activeStepIndex >= 0 ? activeStepIndex + 1 : 1;
	const currentStep = safePrimaryStepStates[activeStepIndex >= 0 ? activeStepIndex : 0];
	const pageName = String(currentStep?.label || '').trim();
	const labels = resolveProgressLabels(progressBarWrapper);
	const startAtZero = isTruthyAttributeValue(progressBarWrapper.getAttribute('data-start-at-zero'));
	const isCompletedState = currentStepIndex >= safePrimaryStepStates.length;
	const progressBarPageCount = isCompletedState
		? safePrimaryStepStates.length
		: (startAtZero ? Math.max(0, currentStepIndex - 1) : currentStepIndex);
	const percentNumber = isCompletedState
		? 100
		: Math.floor((progressBarPageCount / safePrimaryStepStates.length) * 100);
	const percent = `${percentNumber}%`;

	const progressTitleElement = progressBarWrapper.querySelector(PRIMARY_PROGRESS_TITLE_SELECTOR);
	if (progressTitleElement instanceof HTMLElement) {
		renderProgressTitle(progressTitleElement, labels, {
			isCompletedState,
			currentStepIndex,
			stepCount: safePrimaryStepStates.length,
			pageName,
		});
	}

	const liveRegion = resolveProgressLiveRegion(progressBarWrapper);
	if (liveRegion instanceof HTMLDivElement) {
		liveRegion.textContent = buildAccessibleProgressText(labels, {
			isCompletedState,
			currentStepIndex,
			stepCount: safePrimaryStepStates.length,
			pageName,
		});
	}

	const percentageElement = progressBarWrapper.querySelector(PRIMARY_PROGRESS_PERCENT_SELECTOR);
	if (!(percentageElement instanceof HTMLElement)) {
		return;
	}

	Array.from(percentageElement.classList).forEach((className) => {
		if (/^percentbar_\d+$/.test(className)) {
			percentageElement.classList.remove(className);
		}
	});
	percentageElement.classList.add(`percentbar_${percentNumber}`);
	percentageElement.style.width = percent;

	const percentageLabel = percentageElement.querySelector('span');
	if (percentageLabel instanceof HTMLElement) {
		percentageLabel.textContent = percent;
	}
}
